'use client';

import { Fragment, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, TriangleAlert } from 'lucide-react';
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { InterferenceWorkloadCost, InterferenceWorkloadKey } from '@/lib/urm-api';
import { groupedWorkloadMembers, workloadKey } from '../logic';

function evidence(value: number | null | undefined, suffix = ''): string {
  return value == null ? 'insufficient evidence' : `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;
}

function fallbackLabel(level: number): string {
  return ({ 1: 'Full key', 2: 'Action + API label', 3: 'Action', 4: 'Default cost' } as Record<number, string>)[level] ?? `Level ${level}`;
}

export function WorkloadCostView({ costs, workloadKeys }: { costs: InterferenceWorkloadCost[]; workloadKeys: InterferenceWorkloadKey[] }) {
  const [selectedKey, setSelectedKey] = useState(() => costs[0] ? workloadKey(costs[0].workload) : '');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const selected = costs.find((cost) => workloadKey(cost.workload) === selectedKey) ?? costs[0];
  const members = useMemo(
    () => new Map(costs.map((cost) => [workloadKey(cost.workload), groupedWorkloadMembers(cost, workloadKeys)])),
    [costs, workloadKeys],
  );
  const hasSupportedEstimate = costs.some((cost) => cost.confidence_low != null && cost.confidence_high != null && cost.confidence.toLowerCase() !== 'low');

  if (costs.length === 0) {
    return <div className="rounded-lg border bg-muted/20 p-8 text-center text-sm text-muted-foreground">No workload costs could be estimated for this range. Low-incident workloads remain visible whenever the backend can identify them.</div>;
  }

  return (
    <div className="space-y-4">
      {!hasSupportedEstimate && (
        <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <div><p className="font-semibold">No workload weight is supported by enough evidence.</p><p className="mt-1">The values below are fallback defaults for simulation, not recommendations for production configuration.</p></div>
        </div>
      )}
      <details open={hasSupportedEstimate || undefined} className="group">
        <summary className="flex cursor-pointer list-none items-center gap-2 rounded-lg border bg-card px-4 py-3 text-sm font-semibold shadow-sm"><ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />{hasSupportedEstimate ? 'Workload weight details' : `Advanced: show ${costs.length} provisional workload values`}</summary>
      <Card className="mt-3">
        <CardHeader className="pb-3">
          <CardTitle>Workload weight details</CardTitle>
          <CardDescription>A weight is a relative limiter cost inferred from rare cross-user degradation events. Low-confidence fallback values should not be treated as recommendations.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-left text-sm">
              <thead className="border-b text-xs text-muted-foreground">
                <tr>
                  <th className="pb-2 pr-3 font-medium">ResourceType / Action / APILabel</th>
                  <th className="px-3 pb-2 font-medium">Weight</th>
                  <th className="px-3 pb-2 font-medium">95% CI</th>
                  <th className="px-3 pb-2 font-medium">Incidents</th>
                  <th className="px-3 pb-2 font-medium">Confidence</th>
                  <th className="px-3 pb-2 font-medium">Fallback</th>
                  <th className="px-3 pb-2 font-medium">Strongest lag</th>
                  <th className="px-3 pb-2 font-medium">2× risk</th>
                  <th className="px-3 pb-2 font-medium">5× risk</th>
                  <th className="pb-2 pl-3 font-medium">Fixed weight</th>
                </tr>
              </thead>
              <tbody>
                {costs.map((cost) => {
                  const key = workloadKey(cost.workload);
                  const inherited = members.get(key) ?? [];
                  const isExpanded = expanded.has(key);
                  return (
                    <Fragment key={key}>
                      <tr className="border-b align-top hover:bg-muted/30">
                        <td className="py-3 pr-3">
                          <div className="flex items-center gap-1">
                            {inherited.length > 0 && (
                              <button
                                type="button"
                                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${key}`}
                                onClick={() => setExpanded((current) => {
                                  const next = new Set(current);
                                  if (next.has(key)) next.delete(key); else next.add(key);
                                  return next;
                                })}
                                className="rounded p-1 hover:bg-muted"
                              >
                                {isExpanded ? <ChevronDown /> : <ChevronRight />}
                              </button>
                            )}
                            <button type="button" onClick={() => setSelectedKey(key)} className="font-mono text-xs font-medium text-primary hover:underline">{key}</button>
                          </div>
                        </td>
                        <td className="px-3 py-3 font-semibold">{cost.weight.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="px-3 py-3">{cost.confidence_low == null || cost.confidence_high == null ? 'insufficient evidence' : `${cost.confidence_low.toFixed(2)}–${cost.confidence_high.toFixed(2)}`}</td>
                        <td className="px-3 py-3">{cost.incidents}</td>
                        <td className="px-3 py-3"><Badge variant="outline">{cost.confidence}</Badge></td>
                        <td className="px-3 py-3">L{cost.fallback_level} · {fallbackLabel(cost.fallback_level)}</td>
                        <td className="px-3 py-3">{cost.strongest_lag_seconds}s</td>
                        <td className="px-3 py-3">{evidence(cost.rates_at_risk['2x'], ' RPS')}</td>
                        <td className="px-3 py-3">{evidence(cost.rates_at_risk['5x'], ' RPS')}</td>
                        <td className="py-3 pl-3">
                          {cost.fixed_weight_suitable === false ? (
                            <span className="inline-flex items-center gap-1 text-amber-700"><TriangleAlert className="h-4 w-4" />Not suitable</span>
                          ) : cost.fixed_weight_suitable === true ? 'Suitable' : 'insufficient evidence'}
                        </td>
                      </tr>
                      {isExpanded && inherited.map((member) => (
                        <tr key={`${key}:${workloadKey(member)}`} className="border-b bg-muted/20 text-xs">
                          <td className="py-2 pl-10 font-mono">{workloadKey(member)}</td>
                          <td colSpan={9} className="px-3 py-2 text-muted-foreground">Inherits the grouped {key} estimate ({cost.weight.toLocaleString(undefined, { maximumFractionDigits: 2 })}).</td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {selected && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dose-response · {workloadKey(selected.workload)}</CardTitle>
            <CardDescription>Observed candidate request rate versus relative odds of a rare degradation event. The browser does not extrapolate or fit this curve.</CardDescription>
          </CardHeader>
          <CardContent>
            {selected.dose_response.length === 0 ? (
              <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Insufficient evidence for a dose-response curve.</div>
            ) : (
              <div className="h-72" aria-label={`Dose-response chart for ${workloadKey(selected.workload)}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selected.dose_response} margin={{ top: 8, right: 24, left: 8, bottom: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="rps" label={{ value: 'Candidate load (RPS)', position: 'insideBottom', offset: -8 }} />
                    <YAxis label={{ value: 'Relative odds', angle: -90, position: 'insideLeft' }} />
                    <Tooltip formatter={(value) => [`${Number(value).toFixed(2)}×`, 'Relative odds']} />
                    <Line type="monotone" dataKey="relative_odds" stroke="hsl(var(--primary))" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      </details>
    </div>
  );
}
